import * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import debounce from 'lodash/debounce';
import {Button, Flex, Space, Spin, Tooltip} from 'antd';
import {useDispatch} from 'umi';

import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import LeadDropdownFilter from '@/components/Filters/LeadDropdownFilter';
import {LeadModel} from '@/typings/models/Lead';
import LeadName from '@/components/Lead/LeadName';
import {fetchCustomersRequest, fetchLeads} from '@/services/api/lead';
import {hasNextPage, SKIP_TOTAL_SIZE} from '@/utils/pagination';
import styles from './index.less';

import LeadEmail from '@/components/Lead/LeadEmail';
import isEmpty from 'lodash/isEmpty';
import ProductGroupTag from '@/components/ProductGroup/ProductGroupTag';
import {EachRelatedLead} from '@/components/Lead/RelatedLeads';
import {InfoCircleOutlined} from '@ant-design/icons';

const LeadsSelect = ({
                       onChange,
                       getFullObject,
                       initialQuery = {},
                       hasCreateLead,
                       onCreate,
                       createLeadPayload,
                       showEmail = false,
                       groupByCustomer = false,
                       ...props
                     }: any) => {

  const [options, setOptions] = useState<LeadModel[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [currentSearch, setCurrentSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lastRequestId = useRef(0);
  const loadMoreTimeoutRef = useRef<any>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    handleSearch('search', currentSearch);
  }, [JSON.stringify(initialQuery)]);

  const handleSearch = useCallback((mode: 'search' | 'loadMore' = 'search', searchArg?: string) => {
    if (mode === 'loadMore' && (!hasMore || loading)) return;

    setLoading(true);
    const requestId = ++lastRequestId.current;

    const search = searchArg !== undefined ? searchArg : currentSearch;
    const nextPage = mode === 'search' ? 1 : currentPage + 1;

    const query = {
      ...initialQuery,
      search,
      with_leads: 1,
      is_archived: 0,
      sort_by: 'firstname',
      sort_direction: groupByCustomer ? 'asc' : 'desc',
      page: nextPage,
      ...SKIP_TOTAL_SIZE,
    };

    const fetcher = groupByCustomer ? fetchCustomersRequest : fetchLeads;

    fetcher({ query }).then(({ data }) => {
      if (requestId !== lastRequestId.current) return;

      const fetchedItems = data?.data || [];
      if (mode === 'search') {
        setOptions(fetchedItems);
        setCurrentPage(1);
      } else {
        setOptions(prev => [...prev, ...fetchedItems]);
        setCurrentPage(nextPage);
      }

      setHasMore(hasNextPage(data));

      if (isEmpty(fetchedItems)) return;

      let leads = [];
      if (groupByCustomer) {
        fetchedItems.forEach(customer => {
          if (customer.leads?.length > 0) {
            leads = leads.concat(customer.leads);
          }
        });
      } else {
        leads = fetchedItems;
      }

      dispatch({
        type: 'leads/saveLeadsData',
        payload: {
          list: leads,
        },
      });
    }).finally(() => {
      if (requestId === lastRequestId.current) {
        setLoading(false);
      }
    });
  }, [currentPage, hasMore, loading, currentSearch, initialQuery, groupByCustomer, dispatch]);

  const getOptions = () => {
    if (groupByCustomer) {
      return options.map(customer => {
        const leads = customer.leads?.filter(lead => !lead.is_archived)
        if (isEmpty(leads)) return null;
        if (leads?.length === 1) {
          return {
            ...leads[0],
            label: leads[0],
          };
        }
        return {
          label: (
            <Space size={4}>
              <LeadName readonly lead={{ customer }} />
              <small>{customer.email}</small>
              <small>({leads?.length})</small>
            </Space>
          ),
          options: leads.map((lead) => ({
            ...lead,
            grouped: true,
            label: lead,
          })),
        };
      }).filter(Boolean);
    }

    return options.map(opt => ({
      ...opt,
      label: opt,
    }));
  };
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  const debouncedHandleSearch = React.useMemo(() => debounce((search?) => {
    handleSearchRef.current?.('search', search);
  }, 400), []);

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  const handlePopupScroll = (e: any) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight > 0.8 * target.scrollHeight) {
      if (loadMoreTimeoutRef.current) clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = setTimeout(() => {
        handleSearch('loadMore');
      }, 150);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      debouncedHandleSearch.cancel();
      setCurrentSearch('');
      setCurrentPage(1);
      setHasMore(true);
    }
  };
  const openLeadDetails = (e, lead) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
      payload: {
        lead,
      },
    });
  };
  const extractLead = (lead: LeadModel) => lead?.label?.props?.lead || lead?.label || lead || {};

  const labelRender = (lead: LeadModel) => {
    const resolved = extractLead(lead);
    if (!resolved?.customer && !resolved?.id) return undefined;
    return (
      <Space size={4}>
        <LeadName readonly lead={resolved} />
        {showEmail && resolved?.customer?.email && (
          <small>
            <LeadEmail lead={resolved} readonly />
          </small>
        )}
      </Space>
    );
  };

  const optionRender = (lead: LeadModel, options = {}) => {
    if (!lead) return undefined;
    const resolved = extractLead(lead);
    if (resolved.grouped) {
      return <EachRelatedLead hideAppointment lead={resolved} action={false} />;
    }
    return (
      <Flex
        gap={8}
        align={'center'}
        className={styles.leadSelectItemContainer}
        justify={'space-between'}
      >
        <Space>
          <LeadName readonly lead={resolved} />
          {resolved.product_group && (
            <ProductGroupTag productGroup={resolved.product_group} />
          )}
        </Space>
        {showEmail && (
          <small>
            <LeadEmail lead={resolved} readonly />
          </small>
        )}
        <Tooltip title={<F id={'common.details'} defaultMessage={'Details'} />}>
          <Button
            className={styles.openLeadDetailsAction}
            size={'small'}
            color={'lime'}
            variant={'filled'}
            icon={<InfoCircleOutlined />}
            onClick={e => openLeadDetails(e, resolved)}
          />
        </Tooltip>
      </Flex>
    );
  };

  const handleCreateLead = () => {
    const isEmail = currentSearch.includes('@');
    const isPhone = /^\+?[0-9() -]{7,}$/.test(currentSearch);
    let payload: any = {};
    if (isEmail) {
      payload.email = currentSearch;
    } else if (isPhone) {
      payload.phone = currentSearch;
    } else {
      payload.firstname = currentSearch;
    }

    dispatch({
      type: 'leads/toggleAddLeadsDrawer',
      payload: {
        ...(createLeadPayload || {}),
        ...payload,
        filterTabs: ['manual'],
        onCreateLead: (createdLeads: LeadModel[]) => {
          if (createdLeads?.length === 1)
            onCreate && onCreate(createdLeads[0]);
        },
      },
    });
  };

  return (
    <div className={styles.leadSelectContainer}>
      {hasCreateLead && (
        <div className={styles.leadOnTheFlyCreate}>
          <a data-testid="leads-select-add-project-btn" className={'color-secondary'} onClick={handleCreateLead}>
            <F entity={INTL_ENTITY.LEAD} action={INTL_ACTION.ADD} />
          </a>
        </div>
      )}
      <LeadDropdownFilter
        placeholder={<F id={'entity.lead.singular'} defaultMessage={'Lead'} />}
        filterKey={'lead'}
        options={getOptions()}
        onChange={(value, option) =>
          onChange && onChange(getFullObject ? option?.meta : value)
        }
        onSearch={(val) => {
          setCurrentSearch(val);
          debouncedHandleSearch(val);
        }}
        onOpenChange={handleOpenChange}
        loading={loading}
        localSearch={false}
        optionRender={optionRender}
        labelRender={labelRender}
        {...props}
        selectProps={{
          ...(props?.selectProps || {}),
          onPopupScroll: handlePopupScroll,
          dropdownRender: (menu) => (
            <div>
              <Spin spinning={loading}>{menu}</Spin>
              {currentSearch && options.length === 0 && !loading && (
                <>
                  <div
                    style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}
                  >
                    <Button
                      type="link"
                      className={'color-secondary'}
                      block
                      onClick={handleCreateLead}
                    >
                      <Space>
                        <F entity={INTL_ENTITY.LEAD} action={INTL_ACTION.ADD} />
                        <span>"{currentSearch}"</span>
                      </Space>
                    </Button>
                  </div>
                </>
              )}
            </div>
          ),
        }}
      />
    </div>
  );
};

export default LeadsSelect;
